import { GAME_REGISTRY } from "../config/gameRegistry";

const games = Object.freeze(
  [...GAME_REGISTRY].sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
);
const gamesById = Object.freeze(
  games.reduce((map, game) => {
    map[normalizeLookupValue(game.id)] = game;
    return map;
  }, {})
);
const defaultGame = games.find((game) => game.defaultSelected === true) || games[0];

function normalizeLookupValue(value) {
  return String(value || "").trim().toLowerCase();
}

function findByLegacyGameKey(value) {
  const normalized = normalizeLookupValue(value);
  return games.find((game) => normalizeLookupValue(game.legacyGameKey) === normalized) || null;
}

function uniqueGameIds(gameIds) {
  return [...new Set(gameIds.map((gameId) => normalizeLookupValue(gameId)).filter(Boolean))];
}

export const DEFAULT_GAME_ID = defaultGame.id;

export const GameService = Object.freeze({
  get(gameId) {
    return gamesById[normalizeLookupValue(gameId)] || defaultGame;
  },

  getAll() {
    return games;
  },

  getEnabled() {
    return games.filter((game) => game.enabled === true);
  },

  getDisplayName(gameId) {
    return this.get(gameId).displayName;
  },

  normalizeGameId(gameId) {
    const normalized = normalizeLookupValue(gameId);
    if (gamesById[normalized]) return normalized;
    return findByLegacyGameKey(normalized)?.id || DEFAULT_GAME_ID;
  },

  normalizeLegacyGameKey(gameKey) {
    return findByLegacyGameKey(gameKey)?.id || this.normalizeGameId(gameKey);
  },

  getLegacyGameKey(gameId) {
    return this.get(gameId).legacyGameKey;
  },

  exists(gameId) {
    return Boolean(gamesById[normalizeLookupValue(gameId)]);
  },

  isEnabled(gameId) {
    return this.get(gameId).enabled === true;
  },

  isSelectable(gameId) {
    return this.exists(gameId) && this.isEnabled(gameId);
  },

  validate(gameId) {
    const normalized = this.normalizeGameId(gameId);
    return this.exists(normalized) && this.isEnabled(normalized);
  },

  getTournamentTypes(gameId) {
    return this.get(gameId).tournamentTypes || [];
  },

  getSupportedPlatforms(gameId) {
    return this.get(gameId).supportedPlatforms || [];
  },

  getDefaultSelectedGameIds() {
    const selected = games
      .filter((game) => game.enabled === true && game.defaultSelected === true)
      .map((game) => game.id);
    return selected.length > 0 ? selected : [DEFAULT_GAME_ID];
  },

  normalizeSelectedGameIds(gameIds) {
    const rawIds = Array.isArray(gameIds) ? gameIds : [gameIds];
    const normalized = uniqueGameIds(rawIds.map((gameId) => this.normalizeGameId(gameId)))
      .filter((gameId) => this.isSelectable(gameId));
    return normalized.length > 0 ? normalized : this.getDefaultSelectedGameIds();
  },

  toggleSelectedGameId(selectedGameIds, gameId) {
    const normalizedSelected = this.normalizeSelectedGameIds(selectedGameIds);
    const normalizedGameId = this.normalizeGameId(gameId);
    if (!this.isSelectable(normalizedGameId)) return normalizedSelected;

    const isSelected = normalizedSelected.includes(normalizedGameId);
    if (isSelected && normalizedSelected.length === 1) return normalizedSelected;
    if (isSelected) return normalizedSelected.filter((selectedId) => selectedId !== normalizedGameId);
    return [...normalizedSelected, normalizedGameId];
  },

  getSelectionSummary(gameIds) {
    const normalized = this.normalizeSelectedGameIds(gameIds);
    if (normalized.length === 1) return this.getDisplayName(normalized[0]);
    return `${normalized.length} Spiele`;
  },
});
